/* Tatagaltier cart page controller — build-spec §6.
   AJAX quantity/remove/note via the Cart + Section Rendering API, a Terms gate that
   writes cart attributes (terms_accepted + timestamp + version) and blocks checkout
   until accepted, and a manual discount-code apply. Degrades to a plain cart form. */

class TataCart extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.termsVersion = this.dataset.termsVersion || 'v1';
    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('keydown', this.onKey.bind(this));

    const note = this.querySelector('[data-note]');
    if (note) note.addEventListener('input', this.debounce(() => this.updateNote(note.value), 500));

    const form = this.querySelector('#tata-cart-form');
    if (form) form.addEventListener('submit', this.onSubmit.bind(this));

    this.syncCheckoutState();
  }

  /* ---------- events ---------- */
  onClick(e) {
    const up = e.target.closest('[data-qty-up]');
    const down = e.target.closest('[data-qty-down]');
    const remove = e.target.closest('[data-remove]');
    const applyCoupon = e.target.closest('[data-coupon-apply]');
    const offerCode = e.target.closest('[data-coupon-code]');
    const open = e.target.closest('[data-coupon-open]');
    const close = e.target.closest('[data-coupon-close]');

    if (up || down) {
      const item = e.target.closest('[data-line]');
      const input = item.querySelector('[data-qty-input]');
      const next = Math.max(0, parseInt(input.value, 10) + (up ? 1 : -1));
      this.changeLine(item.dataset.line, next);
    } else if (remove) {
      const item = e.target.closest('[data-line]');
      this.changeLine(item.dataset.line, 0);
    } else if (offerCode) {
      this.applyCode(offerCode.dataset.couponCode);
    } else if (applyCoupon) {
      this.applyCoupon();
    } else if (open) {
      this.toggleDrawer(true);
    } else if (close) {
      this.toggleDrawer(false);
    }
  }

  onKey(e) {
    if (e.key === 'Escape' && this.drawerOpen()) this.toggleDrawer(false);
  }

  onChange(e) {
    if (e.target.matches('[data-qty-input]')) {
      const item = e.target.closest('[data-line]');
      this.changeLine(item.dataset.line, Math.max(0, parseInt(e.target.value, 10) || 0));
    } else if (e.target.matches('[data-terms]')) {
      this.updateTerms(e.target.checked);
    }
  }

  onSubmit(e) {
    // Gate checkout on terms acceptance
    const terms = this.querySelector('[data-terms]');
    if (terms && !terms.checked) {
      e.preventDefault();
      const err = this.querySelector('[data-terms-error]');
      if (err) err.hidden = false;
      terms.focus();
    }
  }

  /* ---------- cart mutations ---------- */
  changeLine(line, quantity) {
    this.setBusy(true);
    fetch(`${this.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: Number(line),
        quantity,
        sections: this.sectionId,
        sections_url: window.location.pathname,
      }),
    })
      .then((r) => r.json())
      .then((state) => this.renderSection(state))
      .catch(() => this.setBusy(false));
  }

  updateNote(value) {
    fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note: value }),
    });
  }

  updateTerms(accepted) {
    const attributes = accepted
      ? { terms_accepted: 'true', terms_accepted_at: new Date().toISOString(), terms_version: this.termsVersion }
      : { terms_accepted: '', terms_accepted_at: '', terms_version: '' };
    fetch(`${this.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    this.syncCheckoutState();
  }

  applyCoupon() {
    const input = this.querySelector('[data-coupon-input]');
    const code = (input && input.value || '').trim();
    if (!code) { if (input) input.focus(); return; }
    this.applyCode(code);
  }

  applyCode(code) {
    if (!code) return;
    // Shopify has no cart-AJAX for codes; the /discount route applies then redirects back.
    window.location.href = `${this.root}discount/${encodeURIComponent(code)}?redirect=/cart`;
  }

  /* ---------- coupon drawer ---------- */
  drawer() { return this.querySelector('[data-coupon-drawer]'); }
  drawerOpen() { const d = this.drawer(); return d && !d.hidden; }
  toggleDrawer(open) {
    const d = this.drawer();
    if (!d) return;
    d.hidden = !open;
    document.body.classList.toggle('tata-no-scroll', open);
    if (open) {
      this.querySelector('[data-coupon-close]')?.focus();
    } else {
      this.querySelector('[data-coupon-open]')?.focus();
    }
  }

  /* ---------- rendering ---------- */
  renderSection(state) {
    const html = state.sections && state.sections[this.sectionId];
    const wrapper = document.getElementById(`shopify-section-${this.sectionId}`);
    if (!html || !wrapper) { window.location.reload(); return; }
    // The Section Rendering API returns this section's inner HTML directly.
    // Replacing it re-instantiates <tata-cart> → connectedCallback rebinds.
    wrapper.innerHTML = html;
    document.dispatchEvent(new CustomEvent('tata:cart:updated', { bubbles: true }));
  }

  /* ---------- helpers ---------- */
  syncCheckoutState() {
    const accepted = !!this.querySelector('[data-terms]')?.checked;
    this.querySelectorAll('[data-checkout]').forEach((btn) => {
      btn.toggleAttribute('aria-disabled', !accepted);
    });
    if (accepted) {
      const err = this.querySelector('[data-terms-error]');
      if (err) err.hidden = true;
    }
  }

  setBusy(busy) { this.toggleAttribute('aria-busy', busy); }

  debounce(fn, wait) {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, a), wait); };
  }
}

if (!customElements.get('tata-cart')) customElements.define('tata-cart', TataCart);
